import { useState } from 'react'
import type { Review } from '@/lib/api/reviews'

export interface ReviewLanguageState {
  /** The text to render right now. */
  text: string | null | undefined
  /** True when the review has both an original and a translation to switch between. */
  canToggle: boolean
  /** True when `text` is the customer's own words rather than Google's translation. */
  showingOriginal: boolean
  toggle: () => void
}

/**
 * Choose which version of a translated review to show, and let the reader flip.
 *
 * The original is shown **first**. Google's translation is a flattening of what the
 * customer actually wrote, and the owner replying to them needs their real words and
 * register — the translation is the aid, not the source. Three components render review
 * text and each held its own copy of this logic, all defaulting the other way.
 *
 * Only reviews Google actually translated have an original to fall back to; for
 * everything else `canToggle` is false and `text` is just the review text.
 */
export function useReviewLanguage(review: Review): ReviewLanguageState {
  const canToggle = !!(review.is_translated && review.original_text)
  const [showingOriginal, setShowingOriginal] = useState(true)
  const showOriginal = canToggle && showingOriginal

  return {
    text: showOriginal ? review.original_text : review.text,
    canToggle,
    showingOriginal: showOriginal,
    toggle: () => setShowingOriginal((v) => !v),
  }
}
